import React, { useEffect, useState } from 'react';
import { Cloud, CloudRain, CloudSnow, Sun, CloudFog, Zap, Wind, Droplets, MapPin, IndianRupee, Fuel, Clock, AlertTriangle, Info, CheckCircle2, Loader2 } from 'lucide-react';
import { Badge } from './ui/badge';
import { routeApi } from '../lib/api';

const iconMap = {
  'sunny': Sun,
  'partly-cloudy': Cloud,
  'cloudy': Cloud,
  'rain': CloudRain,
  'heavy-rain': CloudRain,
  'storm': Zap,
  'snow': CloudSnow,
  'fog': CloudFog,
};

const iconColor = {
  'sunny': 'text-[#FFB800]',
  'partly-cloudy': 'text-gray-500',
  'cloudy': 'text-gray-500',
  'rain': 'text-blue-500',
  'heavy-rain': 'text-blue-700',
  'storm': 'text-purple-600',
  'snow': 'text-cyan-500',
  'fog': 'text-gray-400',
};

const formatINR = (n) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(n);

export default function RouteInfoCard({ origin, destination }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!origin || !destination) return;
    setLoading(true);
    routeApi.info(origin, destination).then(setData).catch(() => setData(null)).finally(() => setLoading(false));
  }, [origin, destination]);

  if (!origin || !destination) return null;
  if (loading) {
    return (
      <div className="bg-gradient-to-br from-blue-50 to-cyan-50 border border-blue-200 rounded-xl p-4 flex items-center justify-center gap-2 text-blue-700">
        <Loader2 className="w-4 h-4 animate-spin" />
        <span className="text-sm">Fetching live weather & toll data...</span>
      </div>
    );
  }
  if (!data) return null;

  const WxIcon = ({ wx, size = 'w-5 h-5' }) => {
    const Icon = iconMap[wx.icon] || Cloud;
    return <Icon className={`${size} ${iconColor[wx.icon] || 'text-gray-500'}`} />;
  };

  return (
    <div className="bg-gradient-to-br from-blue-50 to-cyan-50 border border-blue-200 rounded-xl p-4 space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-blue-600 flex items-center justify-center">
            <Cloud className="w-4 h-4 text-white" />
          </div>
          <div>
            <p className="text-sm font-bold text-[#0B2545]">Route Intelligence</p>
            <p className="text-[10px] text-gray-600">Live weather • FASTag toll • Fuel estimate</p>
          </div>
        </div>
        <Badge className="bg-blue-600 text-white border-0">LIVE</Badge>
      </div>

      {/* Route summary */}
      <div className="grid grid-cols-3 gap-2 bg-white/70 rounded-lg p-2">
        <div>
          <p className="text-[10px] text-gray-500">Distance</p>
          <p className="text-sm font-bold text-[#0B2545]">{data.distance_km} km</p>
        </div>
        <div>
          <p className="text-[10px] text-gray-500 flex items-center gap-1"><Clock className="w-3 h-3" />ETA</p>
          <p className="text-sm font-bold text-[#0B2545]">{data.eta_hours}h</p>
        </div>
        <div>
          <p className="text-[10px] text-gray-500">Route</p>
          <p className="text-sm font-bold text-[#0B2545] truncate">{origin.split(',')[0]} → {destination.split(',')[0]}</p>
        </div>
      </div>

      {/* Weather along route */}
      <div>
        <p className="text-[10px] font-semibold text-gray-600 uppercase mb-2">Weather Along Route</p>
        <div className="grid grid-cols-3 gap-2">
          {[
            { label: 'Origin', wx: data.weather.origin, city: origin.split(',')[0] },
            { label: 'Midway', wx: data.weather.midpoint, city: '—' },
            { label: 'Destination', wx: data.weather.destination, city: destination.split(',')[0] },
          ].map((s, i) => (
            <div key={i} className="bg-white rounded-lg p-2">
              <div className="flex items-center gap-1.5">
                <WxIcon wx={s.wx} />
                <span className="text-lg font-bold text-[#0B2545]">{Math.round(s.wx.temp)}°C</span>
              </div>
              <p className="text-[10px] text-gray-500 mt-0.5">{s.city} — {s.wx.desc}</p>
              <div className="flex items-center gap-2 mt-1 text-[9px] text-gray-500">
                <span className="flex items-center gap-0.5"><Wind className="w-2.5 h-2.5" />{Math.round(s.wx.wind)}km/h</span>
                <span className="flex items-center gap-0.5"><Droplets className="w-2.5 h-2.5" />{Math.round(s.wx.humidity)}%</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Toll + Fuel */}
      <div className="grid grid-cols-2 gap-2">
        <div className="bg-white rounded-lg p-3">
          <div className="flex items-center justify-between mb-1">
            <p className="text-[10px] font-semibold text-gray-600 uppercase flex items-center gap-1"><IndianRupee className="w-3 h-3" />FASTag Toll</p>
            <Badge className="bg-[#00A651]/10 text-[#00A651] border-0 text-[9px]">FASTag</Badge>
          </div>
          <p className="text-lg font-extrabold text-[#0B2545] font-[Poppins]">{formatINR(data.toll.estimated_inr)}</p>
          <p className="text-[10px] text-gray-500">~{data.toll.plaza_count} plazas • avg ₹{data.toll.avg_per_plaza}/plaza</p>
        </div>
        <div className="bg-white rounded-lg p-3">
          <div className="flex items-center justify-between mb-1">
            <p className="text-[10px] font-semibold text-gray-600 uppercase flex items-center gap-1"><Fuel className="w-3 h-3" />Fuel</p>
            <Badge className="bg-[#FFB800]/15 text-[#B37800] border-0 text-[9px]">Diesel</Badge>
          </div>
          <p className="text-lg font-extrabold text-[#0B2545] font-[Poppins]">{formatINR(data.fuel_estimate_inr)}</p>
          <p className="text-[10px] text-gray-500">4 kmpl · ₹95/L</p>
        </div>
      </div>

      {/* Advisories */}
      {data.advisories.length > 0 && (
        <div className="space-y-1.5">
          {data.advisories.map((a, i) => {
            const styles = a.level === 'warning' ? 'bg-orange-50 border-orange-200 text-orange-800' : a.level === 'success' ? 'bg-[#00A651]/10 border-[#00A651]/20 text-[#008a43]' : 'bg-blue-50 border-blue-200 text-blue-800';
            const Icon = a.level === 'warning' ? AlertTriangle : a.level === 'success' ? CheckCircle2 : Info;
            return (
              <div key={i} className={`flex items-start gap-2 border rounded-lg px-2.5 py-1.5 text-[11px] ${styles}`}>
                <Icon className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                <span>{a.text}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
